import React, { useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import BotDetector from './components/seo/BotDetector';
import CanonicalLink from './components/seo/CanonicalLink';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import NotFound from './pages/NotFound';
import Explore from './pages/Explore';
import Notifications from './pages/Notifications';
import About from './pages/About';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Contact from './pages/Contact';
import Search from './pages/Search';
import Messages from './pages/Messages';
import Settings from './pages/Settings';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import FollowListPage from './pages/FollowListPage';
import ExplorePeoplePage from './pages/ExplorePeoplePage';
import HashtagPage from './pages/HashtagPage';
import ExploreHashtagsPage from './pages/ExploreHashtagsPage';
import HashtagAnalytics from './pages/admin/HashtagAnalytics';
import AdminPanel from './pages/AdminPanel';
import PostPage from './pages/PostPage';

// Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import PrivateRoute from './components/auth/PrivateRoute';
import OfflineIndicator from './components/common/OfflineIndicator';
import ToastContainer from './components/notifications/ToastContainer';
import HotToaster from './components/notifications/HotToaster';
import InstallPrompt from './components/common/InstallPrompt';

// Context
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import AuthContext from './contexts/AuthContext';

// Componente para rutas de invitados (redirige a / si ya está autenticado)
const GuestRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useContext(AuthContext);
  if (loading) return null; // No mostrar nada mientras carga
  return currentUser ? <Navigate to="/" /> : <>{children}</>;
};

function App() {

  // Asegurarse de que la configuración de idioma se aplique correctamente
  document.documentElement.lang = localStorage.getItem('i18nextLng') || 'es';

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <CanonicalLink />
          <BotDetector>
            <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
              <Navbar />
              <main className="flex-grow container mx-auto px-4 py-6">
                {/* Indicador de modo offline */}
                <OfflineIndicator />
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
                  <Route path="/register" element={<GuestRoute><Register /></GuestRoute>} />
                  <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  {/* Ruta tradicional para perfiles */}
                  <Route
                    path="/profile/:userId"
                    element={
                      <PrivateRoute>
                        <Profile />
                      </PrivateRoute>
                    }
                  />

                  {/* Ruta SEO-friendly para perfiles */}
                  <Route
                    path="/usuario/:userSlug"
                    element={
                      <PrivateRoute>
                        <Profile />
                      </PrivateRoute>
                    }
                  />
                  <Route path="/profile/:userId/followers" element={<FollowListPage />} />
                  <Route path="/profile/:userId/following" element={<FollowListPage />} />
                  <Route path="/explore" element={<Explore />} />
                  <Route
                    path="/notifications"
                    element={
                      <PrivateRoute>
                        <Notifications />
                      </PrivateRoute>
                    }
                  />
                  <Route path="/search" element={<Search />} />
                  <Route
                    path="/messages"
                    element={
                      <PrivateRoute>
                        <Messages />
                      </PrivateRoute>
                    }
                  />
                  <Route path="/about" element={<About />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route
                    path="/settings"
                    element={
                      <PrivateRoute>
                        <Settings />
                      </PrivateRoute>
                    }
                  />
                  <Route path="/explore/people" element={<ExplorePeoplePage />} />
                  <Route path="/hashtag/:tag" element={<HashtagPage />} />
                  <Route path="/explore/hashtags" element={<ExploreHashtagsPage />} />
                  <Route path="/post/:postId" element={<PostPage />} />
                  <Route path="/admin/hashtags" element={<HashtagAnalytics />} />
                  <Route
                    path="/admin"
                    element={
                      <PrivateRoute>
                        <AdminPanel />
                      </PrivateRoute>
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
              <Footer />
            </div>
            <InstallPrompt />
            {/* Contenedor de notificaciones tipo toast */}
            <ToastContainer />
            <HotToaster />

          </BotDetector>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
